import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';

type TurnstileWidgetProps = {
  siteKey: string;
  onToken: (token: string) => void;
  onExpire?: () => void;
  onError?: () => void;
};

// window.turnstile is typed globally in src/types/turnstile.d.ts (which includes
// reset()); no local re-declaration — a narrower duplicate here shadowed it and
// broke turnstile.reset() typing in CaptchaModal.

const TurnstileWidget: React.FC<TurnstileWidgetProps> = ({
  siteKey,
  onToken,
  onExpire,
  onError
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const ensureScript = async () => {
      if (window.turnstile) return;

      const existing = document.querySelector(
        'script[data-turnstile="true"]'
      ) as HTMLScriptElement | null;

      if (existing) {
        await new Promise<void>((resolve) => {
          if (window.turnstile) return resolve();
          existing.addEventListener('load', () => resolve(), { once: true });
          existing.addEventListener('error', () => resolve(), { once: true });
        });
        return;
      }

      await new Promise<void>((resolve) => {
        const script = document.createElement('script');
        script.src =
          'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        script.defer = true;
        script.dataset.turnstile = 'true';
        script.onload = () => resolve();
        script.onerror = () => resolve();
        document.head.appendChild(script);
      });
    };

    const clearContainer = (el: HTMLElement) => {
      while (el.firstChild) el.removeChild(el.firstChild);
    };

    const renderWidget = async () => {
      await ensureScript();
      if (!mounted) return;
      if (!ref.current) return;
      if (!window.turnstile) return;

      // Remove previous widget instance (Turnstile-managed)
      if (widgetIdRef.current) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {}
        widgetIdRef.current = null;
      }

      // Clear container children safely (no innerHTML)
      clearContainer(ref.current);

      widgetIdRef.current = window.turnstile.render(ref.current, {
        sitekey: siteKey,
        theme: 'dark',
        callback: (token: string) => onToken(token),
        'expired-callback': () => onExpire?.(),
        'error-callback': () => onError?.()
      });
    };

    renderWidget();

    return () => {
      mounted = false;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {}
      }
      if (ref.current) clearContainer(ref.current);
    };
  }, [siteKey, onToken, onExpire, onError]);

  return (
    <Box
      ref={ref}
      sx={{ mt: 1.6, display: 'flex', justifyContent: 'center' }}
    />
  );
};

export default TurnstileWidget;
